// Apollo v4 entry points: hooks + provider live at @apollo/client/react.
// Consumers import from `@anchorage/graphql/react` instead of the underlying
// path so the v3→v4 path move stayed a one-line change here. See CEXP-19965.
//
// This file is the v3 → v4 ADAPTER LAYER. It is NOT a masking shim —
// every error is still surfaced via `result.error`, the `onError` callback
// (still valid on `useMutation` in v4), the Apollo `ErrorLink`, and Sentry.
// What the adapter does:
//
//   1. `useLazyQuery`:
//      a) v4 strips init-time `variables` from the exec call. Many callers
//         set `variables` only at hook init and call exec with no args.
//         The wrapped exec merges init `variables` into each exec call so
//         consumers don't need rewrites (call-site `variables` still wins).
//      b) v4 *rejects* the returned promise on error (v3 resolved with
//         `{data, error}`). The wrapped exec catches the rejection and
//         resolves with `{data: undefined}`. The error is STILL on
//         `result.error` from the hook's tuple — consumers handle it the
//         same way they did in v3.
//
//   2. `useMutation`:
//      v4 ALWAYS rejects the bound mutate fn's promise on error, even when
//      an `onError` is set. v3 only rejected when NO `onError` handled it
//      (it resolved with `{data, error}` otherwise). The wrapped mutate fn
//      reproduces v3: it re-throws when no call- or hook-level `onError`
//      handled the error, and resolves with `{data: undefined, error}` when
//      one did — so `try/catch` callers still see the throw and
//      `onError`/`result.error` callers still work. v4 already invoked any
//      hook-level `onError` before throwing, so only the call-level one is
//      fired here.
//
// Lifecycle emulation for `onCompleted` / `onError` on queries was removed
// — the `@anchorage/restricted-syntax/no-lifecycle-callbacks-on-queries`
// lint rule blocks both options on `useQuery` / `useLazyQuery` /
// `usePollingQuery` / `usePaginatedQuery`. Consumers mirror those side
// effects via `useEffect` on `data` / `error`. `useMutation` keeps both.
import type { DocumentNode, OperationVariables } from '@apollo/client';
import {
  type LazyQueryHookOptions,
  type MutationHookOptions,
  type MutationTuple as ApolloMutationTuple,
  type QueryHookOptions,
  useLazyQuery as useLazyQueryApollo,
  useMutation as useMutationApollo,
  useQuery as useQueryApollo,
} from '@apollo/client/react';
import { useMemo, useRef } from 'react';

export { ApolloProvider, useApolloClient } from '@apollo/client/react';

export type ShimQueryOptions<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
> = QueryHookOptions<TData, TVariables>;

// v4 types the result `data` as a discriminated union over `dataState`
// (empty→undefined, partial→DeepPartial, streaming/complete→TData). Collapse
// it back to the v3-flat `data: TData | undefined` at the shim boundary so the
// hundreds of existing call sites keep their v3 data-handling. `dataState` is
// dropped from the surfaced type; the runtime object still carries it for
// anyone who opts into the stricter typing later.
export type ShimQueryResult<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
> = Omit<useQueryApollo.Result<TData, TVariables>, 'data' | 'dataState'> & {
  data: TData | undefined;
};

export const useQuery = <
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: Parameters<typeof useQueryApollo<TData, TVariables>>[0],
  options?: ShimQueryOptions<TData, TVariables>,
): ShimQueryResult<TData, TVariables> =>
  // Apollo v4's `useQuery` typing varies its rest-parameter shape based on
  // whether `TVariables` has required fields — passing `options?` directly
  // can't satisfy both branches. Cast through so consumers keep one
  // ergonomic signature.
  useQueryApollo<TData, TVariables>(query, options as never) as ShimQueryResult<
    TData,
    TVariables
  >;

// v4 `useLazyQuery.Options` dropped the `variables` field (variables now
// go only on the execute call). Many call sites still pass `variables` at
// hook init for parity with v3. Accept it here and merge it into each
// exec call in the wrapped exec below.
/**
 * The AbortError retry in `useLazyQuery` only compensates for a test-harness
 * timing race; in production a genuine unmount-abort must NOT fire a second
 * request, so the retry is gated on this.
 *
 * Read through a computed key: bundlers substitute a literal for
 * `process.env.NODE_ENV`, so a direct reference is frozen to whatever NODE_ENV
 * this package was BUILT with (`development`) and the gate never opens in a
 * consumer's test run. A dynamic index cannot be inlined.
 */
const readEnv = (key: string): string | undefined =>
  (globalThis as { process?: { env?: Record<string, string | undefined> } })
    .process?.env?.[key];

const isTestEnv =
  readEnv('NODE_ENV')?.toLowerCase() === 'test' ||
  readEnv('VITEST') !== undefined;

export type ShimLazyQueryOptions<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
> = LazyQueryHookOptions<TData, TVariables> & {
  variables?: TVariables;
  // v4 only accepts `context` on exec options. v3 (and many call sites)
  // pass it at hook init too; the shim's wrapped exec merges it in.
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  context?: any;
};

// v3-compat: the wrapped exec (below) merges init `variables` and tolerates
// being called with no args, `{}`, or `{ variables }` — mirroring v3's
// lazy-exec ergonomics and the `ShimMutateFn` relaxation used for mutations.
// v4's raw exec type requires fully-shaped `variables` on every call (for a
// query with required variables `ExecOptions.variables` is non-optional),
// which breaks call sites that provide variables at hook init and call
// `exec()`/`exec({})`. Relax the parameter (optional + Partial) while keeping
// the exact return type so `await exec(...)` destructuring is unchanged.
type ShimLazyExecFn<TData, TVariables extends OperationVariables> = (
  execOptions?: Partial<
    Parameters<useLazyQueryApollo.ResultTuple<TData, TVariables>[0]>[0]
  >,
) => ReturnType<useLazyQueryApollo.ResultTuple<TData, TVariables>[0]>;

export const useLazyQuery = <
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: Parameters<typeof useLazyQueryApollo<TData, TVariables>>[0],
  options?: ShimLazyQueryOptions<TData, TVariables>,
): [ShimLazyExecFn<TData, TVariables>, ShimQueryResult<TData, TVariables>] => {
  const [rawExec, result] = useLazyQueryApollo<TData, TVariables>(
    query,
    options as never,
  );

  // Track the latest init `variables` so the wrapped exec always sees the
  // current value, not a stale closure from first render.
  const initVariablesRef = useRef(
    (options as { variables?: TVariables } | undefined)?.variables,
  );
  initVariablesRef.current = (
    options as { variables?: TVariables } | undefined
  )?.variables;

  const wrappedExec = useMemo(() => {
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    const isAbortError = (err: any): boolean =>
      err != null &&
      (err.name === 'AbortError' ||
        String(err.message || '').includes('operation was aborted'));

    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    const fn = (execOptions?: any) => {
      const mergedVariables = {
        ...(initVariablesRef.current ?? {}),
        ...(execOptions?.variables ?? {}),
      };
      // oxlint-disable-next-line @typescript-eslint/no-explicit-any
      const callRaw = () =>
        rawExec({
          ...execOptions,
          variables: mergedVariables,
          // oxlint-disable-next-line @typescript-eslint/no-explicit-any
        } as any) as Promise<any>;

      // v3-compat: resolve to `{data: undefined, error}` instead of
      // rejecting. The error is ALSO on `result.error` (the second tuple
      // element) and any `onError` set in the hook init still fires, but
      // many callers destructure `error` off the exec call's own return
      // value (matching v3's exec, which resolved with `{data, error}`
      // rather than rejecting) — the resolved object must carry it too.
      //
      // AbortError retry (TEST ENV ONLY): v4's `ObservableQuery` rejects with
      // `AbortError` when the in-flight subscription is unsubscribed before
      // any value emits. In React 19 + testing-library, AsyncSelect-style
      // components that fire `loadOptions` from a useEffect can hit this when
      // the effect's cleanup races MockLink's asapScheduler microtask — the
      // first exec ends up aborted with no data even though the mock matched.
      // One silent retry recovers the response. This is a test-timing artifact
      // of MockLink's scheduler, so the retry is gated to the test env: in
      // production a genuine unmount-abort must not trigger a second request.
      // oxlint-disable-next-line @typescript-eslint/no-explicit-any
      return (
        callRaw()
          // oxlint-disable-next-line @typescript-eslint/no-explicit-any
          .catch((err: any) =>
            isTestEnv && isAbortError(err) ? callRaw() : Promise.reject(err),
          )
          // oxlint-disable-next-line @typescript-eslint/no-explicit-any
          .catch((err: any) => ({ data: undefined, error: err })) as any
      );
    };
    return fn as unknown as ShimLazyExecFn<TData, TVariables>;
  }, [rawExec]);

  return [wrappedExec, result as ShimQueryResult<TData, TVariables>];
};

// v3-compat: relax the bound mutate fn so consumers can pass `{}`,
// `{ variables }`, `{ refetchQueries, ... }`, OR v3-style `{ onCompleted,
// onError }` at call time. v4's "modern" signatures require fully-shaped
// variables on each call AND dropped per-call `onCompleted` / `onError`
// (those are init-only in v4). The wrapped exec intercepts the
// per-call callbacks and applies them via `.then` / `.catch`, preserving
// v3 ergonomics. It resolves `{ data, error }` whenever v4 resolves — an
// `onError` handler is irrelevant to that — and re-throws only on v4's own
// rejection with no `onError`, matching v3.
type ShimMutateOptions<TData, TVariables extends OperationVariables> = {
  variables?: TVariables;
  onCompleted?: (data: TData) => void;
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  onError?: (error: any) => void;
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
} & Record<string, any>;

type ShimMutateFn<TData, TVariables extends OperationVariables> = (
  options?: ShimMutateOptions<TData, TVariables>,
) => Promise<{ data: TData | undefined | null; error?: Error }>;

type ShimMutationTuple<TData, TVariables extends OperationVariables> = [
  ShimMutateFn<TData, TVariables>,
  ApolloMutationTuple<TData, TVariables>[1],
];

export const useMutation = <
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
>(
  mutation: Parameters<typeof useMutationApollo<TData, TVariables>>[0],
  options?: MutationHookOptions<TData, TVariables>,
): ShimMutationTuple<TData, TVariables> => {
  const [rawMutate, result] = useMutationApollo<TData, TVariables>(
    mutation,
    options as never,
  );

  // Mirror hook-level options so the reject path can read the latest onError
  // without adding it to the memo deps (which would churn wrappedMutate).
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const wrappedMutate = useMemo<ShimMutateFn<TData, TVariables>>(() => {
    return (mutateOptions) => {
      const onCompleted = mutateOptions?.onCompleted;
      const onError = mutateOptions?.onError;
      // Strip v3-style callbacks before forwarding to v4's strict mutate.
      const forwarded = mutateOptions
        ? (() => {
            const { onCompleted: _c, onError: _e, ...rest } = mutateOptions;
            return rest;
          })()
        : undefined;
      // oxlint-disable-next-line @typescript-eslint/no-explicit-any
      const promise = rawMutate(forwarded as any) as Promise<{
        data?: TData | null;
        error?: Error;
      }>;
      return promise
        .then((res) => {
          // With `errorPolicy: 'all'` v4 resolves (instead of rejecting)
          // and surfaces the error alongside `data`. Preserve `res.error`
          // here so consumers can `if (error)` after the await.
          //
          // Gate on `res.error`, not on `onError` being set: `onCompleted`
          // must not fire on a resolved-with-error result nobody listens for.
          if (res.error) {
            onError?.(res.error);
          } else if (res.data && onCompleted) {
            onCompleted(res.data);
          }
          return { data: res.data, error: res.error };
        })
        .catch((error: Error) => {
          // v4 always rejects on error, even when an onError is set; v3 only
          // threw when NO onError handled it. Reproduce v3 so `try/catch`
          // callers (no onError) still see the throw. v4 already invoked any
          // hook-level onError before throwing, so only fire the call-level one.
          if (onError) {
            onError(error);
          }
          if (onError || optionsRef.current?.onError) {
            return { data: undefined, error };
          }
          throw error;
        });
    };
  }, [rawMutate]);

  return [wrappedMutate, result];
};

// Result / exec / option types re-exposed as shim-owned aliases that point at
// the non-deprecated v4 *namespaced* types (`useQuery.Result`, etc.). The
// top-level `@apollo/client/react` re-exports (`QueryResult`, `MutationResult`,
// `LazyQueryResult`, `LazyQueryExecFunction`, `QueryHookOptions`) are all
// `@deprecated` in v4 — a pure re-export propagated that `@deprecated` JSDoc to
// every consumer of `@anchorage/graphql/react` (SonarQube S1874 across
// frontoffice/operations/etc.). These aliases are STRUCTURALLY IDENTICAL — the
// deprecated types are themselves defined as `= useQuery.Result<…>` etc. — but
// carry no `@deprecated` tag, so consumers import a clean symbol. CEXP-19965.
export type QueryResult<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
> = ShimQueryResult<TData, TVariables>;

export type QueryOptions<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
> = useQueryApollo.Options<TData, TVariables>;

export type LazyQueryResult<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
> = useLazyQueryApollo.Result<TData, TVariables>;

export type LazyQueryResultTuple<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
> = useLazyQueryApollo.ResultTuple<TData, TVariables>;

export type LazyQueryExecFunction<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
> = useLazyQueryApollo.ExecFunction<TData, TVariables>;

export type MutationResult<TData = unknown> = useMutationApollo.Result<TData>;

export type LazyQueryOptions<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
> = useLazyQueryApollo.Options<TData, TVariables>;

export type MutationOptions<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
> = useMutationApollo.Options<TData, TVariables>;

export type MutationFunctionOptions<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
> = useMutationApollo.MutationFunctionOptions<TData, TVariables>;

// Resolution of the shim mutate fn (`{ data, error }`); `wrappedMutate` above
// documents when it resolves vs rejects. Apollo's `FetchResult` is wrong here:
// `@deprecated` in v4 (S1874), and the shim exposes no `errors`/`extensions`.
export type MutateResult<TData = unknown> = Awaited<
  ReturnType<ShimMutateFn<TData, OperationVariables>>
>;

// Public alias for the shim-shape `useMutation` return tuple. v3 codegen
// output referenced `Apollo.MutationTuple`; the shim emits the same
// signature so consumer hooks declaring
// `MutationTuple<TData, TVars>` still type-check after the v4 flip.
export type MutationTuple<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
> = ShimMutationTuple<TData, TVariables>;

// Public alias for the shim-shape bound mutate fn. v3 codegen output
// referenced `Apollo.MutationFunction` for this exact role; the shim
// emits the same signature so consumer hooks declaring
// `MutationFunction<TData, TVars>` still type-check.
export type MutationFunction<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
> = ShimMutateFn<TData, TVariables>;

export const getOperationName = (doc: DocumentNode): string | null => {
  for (const def of doc.definitions) {
    if (def.kind === 'OperationDefinition' && def.name) {
      return def.name.value;
    }
  }
  return null;
};
